import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type StatusKey = 'awaiting_payment' | 'processing' | 'success' | 'failed' | 'refunded' | 'expired';

interface Transaction {
  id: string;
  type: string;
  date: string;
  status: StatusKey;
  game: {
    name: string;
    image: string;
  };
  product: string;
  total: number;
}

interface TransactionCardProps {
  transaction: Transaction;
}

// Status colors keyed off the raw enum so the look stays consistent when
// the locale flips. Labels come from i18n; colors stay here.
const STATUS_COLORS: Record<StatusKey, string> = {
  success: 'bg-green-100 text-green-800',
  awaiting_payment: 'bg-yellow-100 text-yellow-800',
  failed: 'bg-red-100 text-red-800',
  processing: 'bg-blue-100 text-blue-800',
  refunded: 'bg-purple-100 text-purple-800',
  expired: 'bg-gray-200 text-gray-700',
};

const TransactionCard: React.FC<TransactionCardProps> = ({ transaction }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('tx');
  const { t: tStatus } = useTranslation('status');

  const colorClass = STATUS_COLORS[transaction.status] ?? 'bg-gray-100 text-gray-800';

  return (
    <div
      className="bg-white rounded-lg shadow-md border border-gray-200 p-4 md:p-6 hover:shadow-lg transition-shadow cursor-pointer"
      onClick={() => navigate(`/payment/${transaction.id}`)}
    >
      {/* Top Row: Type, Date, Status */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        {/* Left: Type */}
        <span className="text-sm md:text-base font-semibold text-gray-900">
          {transaction.type}
        </span>

        {/* Center: Date */}
        <span className="text-sm text-gray-600 order-3 md:order-2 w-full md:w-auto">
          {transaction.date}
        </span>

        {/* Right: Status */}
        <span
          className={`px-3 py-1 rounded-full text-xs md:text-sm font-medium order-2 md:order-3 ${colorClass}`}
        >
          {tStatus(transaction.status)}
        </span>
      </div>

      {/* Bottom Row: Game Image, Product Info, Total */}
      <div className="flex items-center gap-4">
        {/* Game Image */}
        {transaction.game.image ? (
          <img
            src={transaction.game.image}
            alt={transaction.game.name}
            className="w-14 h-14 md:w-16 md:h-16 rounded-lg object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-14 h-14 md:w-16 md:h-16 rounded-lg bg-gray-200 flex-shrink-0" />
        )}

        {/* Product Info */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 text-sm md:text-base truncate">
            {transaction.game.name}
          </h3>
          <p className="text-sm text-gray-600 truncate">{transaction.product}</p>
        </div>

        {/* Total */}
        <div className="text-right flex-shrink-0">
          <p className="text-xs text-gray-600 mb-1">{t('card_total')}</p>
          <p className="text-base md:text-lg font-bold text-primary">
            USD {transaction.total.toFixed(2)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default TransactionCard;
